// @ts-nocheck
import { useEffect, useState, useRef, useCallback } from 'react';

import { MagnifyingGlass, Clock } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { Text, XStack, YStack } from 'tamagui';

import { useIsMobile } from '../../hooks/useIsMobile';
import { useMotion } from '../../hooks/useMotion';
import { SEARCH_QUERIES } from './constants';

export function SearchDemo() {
  const [activeQueryIndex, setActiveQueryIndex] = useState(0);
  const [typedText, setTypedText] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [phase, setPhase] = useState<'typing' | 'showing' | 'clearing'>('typing');
  const isMobile = useIsMobile();
  const { sectionReveal } = useMotion();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const currentQuery = SEARCH_QUERIES[activeQueryIndex];

  const startCycle = useCallback(() => {
    setPhase('typing');
    setShowResults(false);
    setTypedText('');

    const query = SEARCH_QUERIES[activeQueryIndex].query;
    let charIndex = 0;

    const typeChar = () => {
      if (charIndex < query.length) {
        setTypedText(query.slice(0, charIndex + 1));
        charIndex++;
        timerRef.current = setTimeout(typeChar, 40 + Math.random() * 30);
      } else {
        timerRef.current = setTimeout(() => {
          setShowResults(true);
          setPhase('showing');
          timerRef.current = setTimeout(() => {
            setPhase('clearing');
            setShowResults(false);
            timerRef.current = setTimeout(() => {
              setActiveQueryIndex((prev) => (prev + 1) % SEARCH_QUERIES.length);
            }, 400);
          }, 3500);
        }, 300);
      }
    };

    timerRef.current = setTimeout(typeChar, 500);
  }, [activeQueryIndex]);

  useEffect(() => {
    startCycle();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [startCycle]);

  return (
    <YStack
      id="features"
      paddingVertical={120}
      paddingHorizontal="$5"
      backgroundColor="$color2"
      borderTopWidth={1}
      borderBottomWidth={1}
      borderColor="$borderColor"
    >
      <YStack maxWidth={800} alignSelf="center" width="100%">
        <motion.div
          {...sectionReveal}
          viewport={{ once: true }}
        >
          <YStack
            borderRadius="$4"
            borderWidth={1}
            borderColor="$borderColor"
            backgroundColor="$color3"
            overflow="hidden"
          >
            {/* Search input area */}
            <XStack
              paddingHorizontal={isMobile ? '$4' : '$5'}
              paddingVertical="$4"
              alignItems="center"
              gap="$3"
              borderBottomWidth={1}
              borderColor="$borderColor"
            >
              <MagnifyingGlass size={22} color="var(--color8)" />
              <Text
                fontSize={isMobile ? 16 : 18}
                color="$color12"
                fontWeight="400"
                flex={1}
              >
                {typedText}
                <motion.span
                  animate={{ opacity: [1, 0] }}
                  transition={{ duration: 0.6, repeat: Infinity, repeatType: 'reverse' }}
                  style={{
                    display: 'inline-block',
                    width: 2,
                    height: 20,
                    backgroundColor: 'var(--color11)',
                    marginLeft: 2,
                    verticalAlign: 'text-bottom',
                  }}
                />
              </Text>
              <XStack
                paddingHorizontal="$2"
                paddingVertical={2}
                borderRadius="$3"
                backgroundColor="$color3"
              >
                <Text fontSize={11} color="$color8" fontWeight="500">
                  Enter
                </Text>
              </XStack>
            </XStack>

            {/* Results area */}
            <YStack
              paddingVertical="$2"
              height={280}
              overflow="hidden"
            >
              <AnimatePresence>
                {showResults &&
                  currentQuery.results.map((result, index) => (
                    <motion.div
                      key={result.title}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 10 }}
                      transition={{ delay: index * 0.08, duration: 0.3 }}
                    >
                      <XStack
                        paddingHorizontal={isMobile ? '$4' : '$5'}
                        paddingVertical="$3"
                        gap="$3"
                        alignItems="flex-start"
                        hoverStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.03)' }}
                        style={{ transition: 'background-color 0.15s ease', cursor: 'pointer' } as any}
                      >
                        <YStack flex={1} gap="$1">
                          <XStack
                            alignItems="center"
                            gap="$2"
                            flexWrap="wrap"
                          >
                            <Text
                              fontSize={14}
                              fontWeight="600"
                              color="$color12"
                              numberOfLines={1}
                            >
                              {result.title}
                            </Text>
                            <Text fontSize={10} color="$color8" fontWeight="500">
                              {result.source}
                            </Text>
                          </XStack>
                          <Text
                            fontSize={13}
                            color="$color9"
                            numberOfLines={1}
                          >
                            {result.snippet}
                          </Text>
                          <XStack alignItems="center" gap="$1" marginTop={2}>
                            <Clock size={11} color="var(--color8)" />
                            <Text fontSize={11} color="$color8">
                              {result.time}
                            </Text>
                          </XStack>
                        </YStack>
                      </XStack>
                    </motion.div>
                  ))}
              </AnimatePresence>
              {!showResults && (
                <YStack
                  flex={1}
                  alignItems="center"
                  justifyContent="center"
                  opacity={0.4}
                >
                  <Text fontSize={14} color="$color8">
                    {phase === 'typing' ? 'Searching...' : 'Loading results...'}
                  </Text>
                </YStack>
              )}
            </YStack>
          </YStack>
        </motion.div>
      </YStack>
    </YStack>
  );
}
